import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';

const resolvePaymentStatus = (order) => {
  const paymentStatus = String(order?.paymentStatus || order?.payment_status || '').trim().toUpperCase();
  if (paymentStatus) {
    if (['COMPLETED', 'PAID', 'SETTLED'].includes(paymentStatus)) return 'PAID';
    return paymentStatus;
  }

  const paymentEvidence = [
    order?.paymentTransactionId,
    order?.payment_transaction_id,
    order?.razorpayPaymentId,
    order?.razorpay_payment_id,
    order?.razorpayOrderId,
    order?.razorpay_order_id,
  ].some((value) => String(value || '').trim().length > 0);
  const orderType = String(order?.orderType || order?.order_type || '').trim().toUpperCase();
  if (orderType && orderType !== 'DINE_IN' && paymentEvidence) return 'PAID';

  const orderStatus = String(order?.orderStatus || order?.order_status || '').trim().toUpperCase();
  if (['COMPLETED', 'PAID', 'SETTLED'].includes(orderStatus)) return 'PAID';

  const paymentMethod = String(order?.paymentMethod || order?.payment_method || '').trim().toUpperCase();
  if (['ONLINE', 'UPI', 'CARD', 'RAZORPAY', 'PAYTM', 'PHONEPE', 'GOOGLEPAY', 'GPAY', 'NETBANKING'].includes(paymentMethod)) {
    return 'PAID';
  }

  return 'UNPAID';
};

const OrderSuccess = () => {
  const { shopCode, orderId } = useParams();
  const history = useHistory();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadOrderDetails();
  }, [orderId]);

  const loadOrderDetails = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/vendor/orders/${orderId}`);
      if (response.data.success) {
        setOrder(response.data.data);
      }
    } catch (error) {
      console.error('Error loading order:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-red-600">Order not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            {/* Success Header */}
            <div className="bg-green-500 text-white text-center py-8">
              <div className="text-6xl mb-4">✓</div>
              <h1 className="text-3xl font-bold mb-2">Order Placed Successfully!</h1>
              <p className="text-green-100">Thank you for your order</p>
            </div>
            
            {/* Order Details */}
            <div className="p-6">
              <div className="mb-6 pb-6 border-b">
                <h2 className="text-xl font-bold text-gray-800 mb-4">Order Details</h2>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Order Number:</span>
                    <p className="font-bold text-gray-800">{order.orderNumber}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Payment Status:</span>
                    <p className={`font-bold ${resolvePaymentStatus(order) === 'PAID' ? 'text-green-600' : 'text-red-600'}`}>
                      {resolvePaymentStatus(order)}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-600">Payment Method:</span>
                    <p className="font-bold text-gray-800">{order.paymentMethod}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Total Amount:</span>
                    <p className="font-bold text-green-600 text-lg">₹{order.totalAmount}</p>
                  </div>
                </div>
              </div>
              
              <div className="mb-6 pb-6 border-b">
                <h3 className="font-bold text-gray-800 mb-2">Delivery Address</h3>
                <p className="text-gray-600 text-sm">{order.deliveryAddress}</p>
                {order.deliveryInstructions && (
                  <p className="text-gray-500 text-xs mt-2">
                    <strong>Delivery Instructions:</strong> {order.deliveryInstructions}
                  </p>
                )}
              </div>
              
              <div className="mb-6">
                <h3 className="font-bold text-gray-800 mb-3">Order Items</h3>
                <div className="space-y-2">
                  {order.orderItems?.map((item, index) => (
                    <div key={index} className="flex justify-between text-sm">
                      <span className="text-gray-700">
                        {item.product?.productName} x {item.quantity}
                      </span>
                      <span className="font-medium text-gray-800">
                        ₹{(item.unitPrice * item.quantity).toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="bg-blue-50 p-4 rounded-lg mb-6">
                <h3 className="font-bold text-blue-900 mb-2">What's Next?</h3>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>✓ Your order has been received</li>
                  <li>• Shop will confirm your order shortly</li>
                  <li>• You'll receive updates on your phone</li>
                  <li>• Delivery will be arranged once order is ready</li>
                </ul>
              </div>
              
              <div className="flex gap-4">
                <button
                  onClick={() => history.push(`/${shopCode}`)}
                  className="flex-1 bg-blue-500 text-white py-3 rounded-lg hover:bg-blue-600 transition font-medium"
                >
                  Continue Shopping
                </button>
                <button
                  onClick={() => window.print()}
                  className="flex-1 bg-gray-200 text-gray-800 py-3 rounded-lg hover:bg-gray-300 transition font-medium"
                >
                  Print Receipt
                </button>
              </div>
              
              <div className="mt-6 text-center">
                <p className="text-sm text-gray-600">
                  Need help? Contact: {order.shop?.contactPhone}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderSuccess;
